'use client'

import { useState, type ReactNode } from 'react'
import { Input, Label } from '@/components/ui'
import { FieldError } from './auth-shell'

/**
 * Password input with the design's text-link show/hide toggle (replaces the
 * old in-field eye icon) and optional helper / inline error lines.
 */
export function PasswordField({
    id,
    label,
    value,
    onChange,
    autoComplete,
    labelEnd,
    helper,
    error,
    required = true,
}: {
    id: string
    label: string
    value: string
    onChange: (value: string) => void
    autoComplete: string
    /** Slot on the label row's right edge (e.g. the "Forgot password?" link). */
    labelEnd?: ReactNode
    helper?: string
    error?: string
    required?: boolean
}) {
    const [show, setShow] = useState(false)
    const errorId = `${id}-error`

    return (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between">
                <Label htmlFor={id}>{label}</Label>
                {labelEnd}
            </div>
            <Input
                id={id}
                type={show ? 'text' : 'password'}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                autoComplete={autoComplete}
                required={required}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                className={error ? 'border-[var(--border-danger)]' : undefined}
            />
            <button
                type="button"
                onClick={() => setShow((v) => !v)}
                aria-pressed={show}
                className="-my-3 inline-flex min-h-11 items-center px-1 -mx-1 text-sm font-medium text-[var(--text-brand)] hover:underline focus-visible:outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
            >
                {show ? 'Hide password' : 'Show password'}
            </button>
            {helper && <p className="text-sm leading-5 text-[var(--text-tertiary)]">{helper}</p>}
            {error && <FieldError id={errorId}>{error}</FieldError>}
        </div>
    )
}
